import {
  QueueSettings,
  QueueSettingsEditValue,
} from '@rossum/api-client/queues';

export type EditValueOptionForm = {
  label: string;
  value: string;
};

/**
 * Flat form representation of a single `uiEditValues` entry. `enumOptions` is
 * always present (even for `string` fields) so React Hook Form keeps stable
 * field state when the user switches `dataType`. It is dropped on save for
 * non-enum fields — see {@link toQueueEditValues}.
 */
export type EditValueForm = {
  id: string;
  label: string;
  required: boolean;
  dataType: 'string' | 'enum';
  enumOptions: EditValueOptionForm[];
};

export type EditModeFormValues = {
  uiEditValues: EditValueForm[];
  uiEditTargetQueueSelectionEnabled: boolean;
  uiEditAlwaysSplitDocument: boolean;
  uiOnEditConfirm: 'annotation_list' | 'edit_next' | 'validate_first';
  uiValidationScreenEnabled: boolean;
};

export const editModeFormDefaults = (
  settings: QueueSettings
): EditModeFormValues => ({
  uiEditValues: (settings.uiEditValues ?? []).map(value => ({
    id: value.id,
    label: value.label,
    required: value.required,
    dataType: value.dataType,
    enumOptions: value.dataType === 'enum' ? value.enumOptions : [],
  })),
  uiEditTargetQueueSelectionEnabled:
    settings.uiEditTargetQueueSelectionEnabled ?? true,
  uiEditAlwaysSplitDocument: settings.uiEditAlwaysSplitDocument ?? false,
  uiOnEditConfirm: settings.uiOnEditConfirm ?? 'validate_first',
  uiValidationScreenEnabled: settings.uiValidationScreenEnabled ?? true,
});

/**
 * Maps the flat form rows back into the `QueueSettingsEditValue` discriminated
 * union, dropping `enumOptions` for `string` fields.
 */
export const toQueueEditValues = (
  values: EditValueForm[]
): QueueSettingsEditValue[] =>
  values.map(value => {
    const base = {
      id: value.id,
      label: value.label,
      required: value.required,
    };

    return value.dataType === 'enum'
      ? { ...base, dataType: 'enum', enumOptions: value.enumOptions }
      : { ...base, dataType: 'string' };
  });
